import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, storage } from '../firebase';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  setDoc,
  addDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import {
  FaSpinner,
  FaVideo,
  FaImage,
  FaCheck,
  FaTimes,
  FaEye,
  FaTag,
  FaUser,
  FaEnvelope,
} from 'react-icons/fa';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'underReview', label: 'Under review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const STATUS_STYLES = {
  submitted: 'border-amber-200 bg-amber-50 text-amber-900',
  underReview: 'border-sky-200 bg-sky-50 text-sky-900',
  approved: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  rejected: 'border-rose-200 bg-rose-50 text-rose-900',
};

const PHOTO_MATCH_WINDOW_MS = 5 * 60 * 1000;

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDate(value) {
  const d = toDate(value);
  return d ? d.toLocaleString() : '—';
}

function shortUserId(uid) {
  if (!uid) return 'XXXX';
  return String(uid).slice(0, 4).toUpperCase();
}

function makeCouponCode(userId) {
  const suffix = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `UGC40-${shortUserId(userId)}${suffix}`;
}

const API_BASE = String(process.env.REACT_APP_API_BASE || 'https://vue-admin-proxy.fly.dev').replace(
  /\/$/,
  ''
);

async function sendPushNotification(userId, title, body, data = {}) {
  if (!userId) return null;
  try {
    const response = await fetch(`${API_BASE}/api/send-push-notification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, title, body, data }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.success) {
      console.warn('UGC push failed:', response.status, result);
      return null;
    }
    return result;
  } catch (error) {
    console.warn('UGC push error:', error);
    return null;
  }
}

function fileUuidFromPath(path) {
  const base = String(path || '').split('/').pop() || '';
  return base.replace(/\.[^.]+$/, '');
}

function mediaPathKey(urlOrPath) {
  if (!urlOrPath) return '';
  const s = String(urlOrPath);
  try {
    if (s.includes('firebasestorage.googleapis.com') || s.includes('storage.googleapis.com')) {
      const decoded = decodeURIComponent(s);
      const m = decoded.match(/\/o\/([^?]+)/) || decoded.match(/\/ugc\/[^?\s]+/);
      if (m) return (m[1] || m[0]).replace(/^\/+/, '');
    }
  } catch (_) {
    /* ignore */
  }
  if (s.startsWith('ugc/')) return s.split('?')[0];
  return s;
}

async function listStorageFiles(prefix) {
  const root = ref(storage, prefix);
  const users = await listAll(root);
  const out = [];
  for (const userFolder of users.prefixes) {
    const files = await listAll(userFolder);
    for (const item of files.items) {
      const [url, meta] = await Promise.all([getDownloadURL(item), getMetadata(item)]);
      out.push({
        path: item.fullPath,
        userId: userFolder.name,
        url,
        updatedAt: meta.updated ? new Date(meta.updated) : new Date(),
        uuid: fileUuidFromPath(item.fullPath),
      });
    }
  }
  return out;
}

function buildStorageSubmissions(videos, images) {
  const imagesByUser = {};
  for (const img of images) {
    if (!imagesByUser[img.userId]) imagesByUser[img.userId] = [];
    imagesByUser[img.userId].push(img);
  }

  const usedImagePaths = new Set();

  return videos
    .map((video) => {
      const candidates = (imagesByUser[video.userId] || [])
        .filter((img) => !usedImagePaths.has(img.path))
        .map((img) => ({
          img,
          delta: Math.abs(img.updatedAt.getTime() - video.updatedAt.getTime()),
        }))
        .filter((x) => x.delta <= PHOTO_MATCH_WINDOW_MS)
        .sort((a, b) => a.delta - b.delta);

      const matched = candidates.slice(0, 6).map((c) => c.img);
      matched.forEach((img) => usedImagePaths.add(img.path));

      return {
        id: video.uuid,
        userId: video.userId,
        userEmail: null,
        campaignId: null,
        campaignName: null,
        mediaURL: video.url,
        mediaType: 'video',
        mediaPath: video.path,
        additionalMediaURLs: matched.map((m) => m.url),
        caption: null,
        status: 'submitted',
        creditPercentage: 40,
        creditApplied: false,
        couponCode: null,
        submittedAt: video.updatedAt,
        reviewedAt: null,
        reviewedBy: null,
        reviewNotes: null,
        source: 'storage',
      };
    })
    .sort((a, b) => (toDate(b.submittedAt)?.getTime() || 0) - (toDate(a.submittedAt)?.getTime() || 0));
}

function isCoveredByFirestore(storageSub, firestoreRows) {
  return firestoreRows.some((row) => {
    if (row.id === storageSub.id) return true;
    const rowKey = mediaPathKey(row.mediaURL || row.mediaPath);
    if (rowKey && (rowKey === storageSub.mediaPath || rowKey.includes(storageSub.id))) return true;
    if (row.mediaURL && storageSub.mediaURL && row.mediaURL === storageSub.mediaURL) return true;
    return false;
  });
}

export default function UGCSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('submitted');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [storageOnlyCount, setStorageOnlyCount] = useState(0);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  useEffect(() => {
    setActivePhotoIndex(0);
    setShowRejectForm(false);
    setRejectNotes('');
  }, [selected?.id]);

  async function loadFirestoreRows() {
    try {
      const q = query(collection(db, 'ugc_submissions'), orderBy('submittedAt', 'desc'));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data(), source: 'firestore' }));
    } catch (e) {
      console.warn('orderBy submittedAt failed, falling back:', e?.message || e);
      const snap = await getDocs(collection(db, 'ugc_submissions'));
      return snap.docs
        .map((d) => ({ id: d.id, ...d.data(), source: 'firestore' }))
        .sort((a, b) => (toDate(b.submittedAt)?.getTime() || 0) - (toDate(a.submittedAt)?.getTime() || 0));
    }
  }

  async function enrichEmails(rows) {
    const missing = [...new Set(rows.filter((r) => r.userId && !r.userEmail).map((r) => r.userId))];
    if (!missing.length) return rows;
    const emailById = {};
    await Promise.all(
      missing.map(async (uid) => {
        try {
          const snap = await getDoc(doc(db, 'users', uid));
          if (snap.exists()) {
            const data = snap.data();
            emailById[uid] = data.email || data.Email || null;
          }
        } catch (_) {
          /* ignore */
        }
      })
    );
    return rows.map((r) => (r.userEmail ? r : { ...r, userEmail: emailById[r.userId] || r.userEmail }));
  }

  async function fetchSubmissions() {
    setLoading(true);
    setError('');
    try {
      const firestoreRows = await loadFirestoreRows();

      let storageSubs = [];
      try {
        const [videos, images] = await Promise.all([
          listStorageFiles('ugc/videos'),
          listStorageFiles('ugc/images'),
        ]);
        storageSubs = buildStorageSubmissions(videos, images);
      } catch (storageErr) {
        console.error('Storage list failed:', storageErr);
        if (!firestoreRows.length) {
          setError('Could not list Storage ugc/ files. Check Storage rules / auth.');
        }
      }

      const missing = storageSubs.filter((s) => !isCoveredByFirestore(s, firestoreRows));
      setStorageOnlyCount(missing.length);

      // Persist Storage-only uploads into Firestore so review/approve work on real docs
      for (const s of missing) {
        try {
          await setDoc(
            doc(db, 'ugc_submissions', s.id),
            {
              id: s.id,
              userId: s.userId,
              userEmail: s.userEmail,
              campaignId: s.campaignId,
              campaignName: s.campaignName,
              mediaURL: s.mediaURL,
              mediaType: s.mediaType,
              additionalMediaURLs: s.additionalMediaURLs,
              caption: s.caption,
              status: 'submitted',
              creditPercentage: 40,
              creditApplied: false,
              couponCode: null,
              submittedAt: s.submittedAt,
              reviewedAt: null,
              reviewedBy: null,
              reviewNotes: null,
              importedFromStorage: true,
              mediaPath: s.mediaPath,
            },
            { merge: true }
          );
        } catch (writeErr) {
          console.error('Failed to backfill submission', s.id, writeErr);
        }
      }

      const refreshed = missing.length ? await loadFirestoreRows() : firestoreRows;
      const merged = await enrichEmails(refreshed.length ? refreshed : [...firestoreRows, ...missing]);

      setSubmissions(merged);
      setSelected((prev) => {
        if (!prev) return null;
        return merged.find((r) => r.id === prev.id) || null;
      });
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load UGC submissions');
    }
    setLoading(false);
  }

  async function persistSubmission(submission, patch) {
    const payload = {
      id: submission.id,
      userId: submission.userId || null,
      userEmail: submission.userEmail || null,
      campaignId: submission.campaignId || null,
      campaignName: submission.campaignName || null,
      mediaURL: submission.mediaURL || null,
      mediaType: submission.mediaType || 'video',
      additionalMediaURLs: submission.additionalMediaURLs || [],
      caption: submission.caption || null,
      creditPercentage: submission.creditPercentage ?? 40,
      creditApplied: submission.creditApplied ?? false,
      couponCode: submission.couponCode || null,
      submittedAt: toDate(submission.submittedAt) || new Date(),
      ...patch,
    };
    await setDoc(doc(db, 'ugc_submissions', submission.id), payload, { merge: true });
  }

  const counts = useMemo(() => {
    const c = { all: submissions.length, submitted: 0, underReview: 0, approved: 0, rejected: 0 };
    for (const s of submissions) {
      if (c[s.status] !== undefined) c[s.status] += 1;
    }
    return c;
  }, [submissions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return submissions.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (s.userEmail || '').toLowerCase().includes(q) ||
        (s.userId || '').toLowerCase().includes(q) ||
        (s.campaignName || '').toLowerCase().includes(q) ||
        (s.caption || '').toLowerCase().includes(q) ||
        (s.couponCode || '').toLowerCase().includes(q) ||
        (s.id || '').toLowerCase().includes(q)
      );
    });
  }, [submissions, statusFilter, search]);

  async function markUnderReview(submission) {
    if (!submission || submission.status !== 'submitted') return;
    try {
      await persistSubmission(submission, {
        status: 'underReview',
        reviewedBy: auth.currentUser?.email || auth.currentUser?.uid || null,
      });
      await fetchSubmissions();
    } catch (e) {
      console.error(e);
      setError('Failed to mark under review');
    }
  }

  async function createInAppNotification(userId, payload) {
    if (!userId) return;
    try {
      await addDoc(collection(db, 'users', userId, 'notifications'), {
        seen: false,
        dateIssued: new Date(),
        ...payload,
      });
    } catch (err) {
      console.error(`Failed to create in-app notification for ${userId}:`, err);
    }
  }

  async function notifyApproval(submission, couponCode) {
    const userId = submission.userId;
    const title = "You've earned 40% off your next campaign!";
    const body = `Thanks for sharing your Vue story. Use code ${couponCode} for 40% off one upcoming campaign.`;
    await Promise.all([
      createInAppNotification(userId, {
        title,
        description: body,
        type: 'coupon',
        couponCode,
        ugcSubmissionId: submission.id,
      }),
      sendPushNotification(userId, title, body, {
        type: 'ugc_approved',
        submissionId: submission.id,
        couponCode,
      }),
    ]);
  }

  async function notifyRejection(submission, notes) {
    const userId = submission.userId;
    const title = 'Share Your Story update';
    const body = notes?.trim()
      ? `Your submission wasn't approved. ${notes.trim()}`
      : "Your Share Your Story submission wasn't approved. Reach out if you have questions.";
    await Promise.all([
      createInAppNotification(userId, {
        title,
        description: body,
        type: 'ugc',
        ugcSubmissionId: submission.id,
        status: 'rejected',
      }),
      sendPushNotification(userId, title, body, {
        type: 'ugc_rejected',
        submissionId: submission.id,
      }),
    ]);
  }

  async function handleApprove(submission) {
    if (!submission) return;
    if (submission.status === 'approved' && submission.couponCode) {
      setError('Already approved with coupon ' + submission.couponCode);
      return;
    }
    if (!window.confirm('Approve this submission and issue a 40% campaign coupon to this user?')) {
      return;
    }

    setActionLoading(true);
    setError('');
    try {
      const userId = submission.userId;
      if (!userId) throw new Error('Submission is missing userId');

      const couponCode = submission.couponCode || makeCouponCode(userId);
      const validFrom = new Date();
      const validTo = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);
      const reviewer = auth.currentUser?.email || auth.currentUser?.uid || null;

      if (!submission.couponCode) {
        await setDoc(doc(db, 'coupons', couponCode), {
          code: couponCode,
          type: 'campaign',
          discountType: 'percentage',
          discountValue: 40,
          validFrom,
          validTo,
          maxRedemptions: 1,
          redemptions: 0,
          active: true,
          applicableTo: [userId],
          createdAt: new Date(),
          source: 'ugc',
          ugcSubmissionId: submission.id,
        });
        await notifyApproval(submission, couponCode);
      }

      await persistSubmission(submission, {
        status: 'approved',
        reviewedAt: new Date(),
        reviewedBy: reviewer,
        creditPercentage: submission.creditPercentage ?? 40,
        couponCode,
        creditApplied: false,
      });

      await fetchSubmissions();
      alert(`Approved. Coupon ${couponCode} created for this user.`);
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to approve submission');
    }
    setActionLoading(false);
  }

  async function handleReject(submission) {
    if (!submission) return;
    setActionLoading(true);
    setError('');
    try {
      const notes = rejectNotes.trim() || null;
      await persistSubmission(submission, {
        status: 'rejected',
        reviewedAt: new Date(),
        reviewedBy: auth.currentUser?.email || auth.currentUser?.uid || null,
        reviewNotes: notes,
      });
      await notifyRejection(submission, notes);
      setShowRejectForm(false);
      setRejectNotes('');
      await fetchSubmissions();
    } catch (e) {
      console.error(e);
      setError('Failed to reject submission');
    }
    setActionLoading(false);
  }

  const photos = selected?.additionalMediaURLs || [];

  return (
    <>
      <div className="f-page-enter mx-auto max-w-[1920px] px-4 pb-10 md:px-6">
        <div className="f-header rounded-xl border border-slate-200/80 shadow-sm">
          <div>
            <h1 className="f-title">Share Your Story</h1>
            <p className="f-subtitle mb-0">
              Review UGC selfie videos &amp; photos, then issue 40% campaign coupons
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
            <span className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 font-medium text-amber-900 tabular-nums">
              {counts.submitted} queued
            </span>
            <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 font-medium tabular-nums">
              {counts.all} total
            </span>
            <button
              type="button"
              className="f-button f-button-secondary !min-h-9 !h-9 !px-3 !text-xs"
              onClick={fetchSubmissions}
              disabled={loading}
            >
              Refresh
            </button>
          </div>
        </div>

        {storageOnlyCount > 0 && !loading && (
          <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
            Imported {storageOnlyCount} Storage upload{storageOnlyCount === 1 ? '' : 's'} into{' '}
            <code className="text-xs">ugc_submissions</code> (caption/campaign were only on-device for
            those). New iOS builds should write Firestore directly.
          </div>
        )}

        {error && (
          <div
            className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
            role="alert"
          >
            <span>{error}</span>
            <button
              type="button"
              className="f-button f-button-secondary shrink-0 !min-h-8 !h-8 !px-3 !text-xs"
              onClick={() => setError('')}
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setStatusFilter(f.value)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                  statusFilter === f.value
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                {f.label}
                <span className="ml-1.5 tabular-nums opacity-70">{counts[f.value] ?? 0}</span>
              </button>
            ))}
          </div>
          <input
            className="f-input w-full sm:max-w-xs"
            placeholder="Search email, campaign, caption…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,380px)_1fr] lg:items-start">
          <div className="f-card !p-0 overflow-hidden">
            <div className="border-b border-slate-100 px-4 py-3">
              <h2 className="text-sm font-semibold text-slate-900">
                Queue <span className="font-normal text-slate-500">({filtered.length})</span>
              </h2>
            </div>
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
                <FaSpinner className="animate-spin" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-16 text-center text-sm text-slate-500">
                No submissions match this filter.
              </div>
            ) : (
              <ul className="max-h-[70vh] divide-y divide-slate-100 overflow-y-auto">
                {filtered.map((s) => {
                  const active = selected?.id === s.id;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelected(s);
                          if (s.status === 'submitted') markUnderReview(s);
                        }}
                        className={`flex w-full flex-col gap-1 px-4 py-3 text-left transition ${
                          active ? 'bg-slate-100' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="truncate text-sm font-semibold text-slate-900">
                            {s.userEmail || s.userId || 'Unknown user'}
                          </span>
                          <span
                            className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                              STATUS_STYLES[s.status] || 'border-slate-200 bg-slate-50 text-slate-700'
                            }`}
                          >
                            {s.status || '—'}
                          </span>
                        </div>
                        <p className="truncate text-xs text-slate-500">
                          {s.campaignName || s.campaignId || (s.importedFromStorage ? 'Imported from Storage' : 'No campaign')}
                        </p>
                        <p className="line-clamp-2 text-xs text-slate-600">
                          {s.caption || (s.importedFromStorage ? 'No caption (Storage import)' : 'No caption')}
                        </p>
                        <p className="text-[11px] text-slate-400">{formatDate(s.submittedAt)}</p>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="f-card min-h-[420px]">
            {!selected ? (
              <div className="flex h-full min-h-[400px] flex-col items-center justify-center gap-2 text-slate-500">
                <FaEye className="text-2xl opacity-40" />
                <p className="text-sm">Select a submission to review</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Review</h2>
                    <p className="mt-1 font-mono text-xs text-slate-400">{selected.id}</p>
                  </div>
                  <span
                    className={`rounded-md border px-2 py-1 text-xs font-semibold uppercase ${
                      STATUS_STYLES[selected.status] || 'border-slate-200 bg-slate-50'
                    }`}
                  >
                    {selected.status}
                  </span>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      <FaUser /> User
                    </div>
                    <p className="truncate text-sm font-medium text-slate-900">
                      {selected.userEmail || '—'}
                    </p>
                    <p className="mt-0.5 truncate font-mono text-[11px] text-slate-500">
                      {selected.userId}
                    </p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      <FaEnvelope /> Campaign
                    </div>
                    <p className="truncate text-sm font-medium text-slate-900">
                      {selected.campaignName || '—'}
                    </p>
                    <p className="mt-0.5 truncate font-mono text-[11px] text-slate-500">
                      {selected.campaignId || '—'}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Caption
                  </h3>
                  <p className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm text-slate-800">
                    {selected.caption || '—'}
                  </p>
                </div>

                <div>
                  <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <FaVideo /> Selfie video
                  </h3>
                  {selected.mediaURL ? (
                    <video
                      key={selected.mediaURL}
                      src={selected.mediaURL}
                      controls
                      playsInline
                      className="max-h-[360px] w-full rounded-xl border border-slate-200 bg-black object-contain"
                    />
                  ) : (
                    <p className="text-sm text-slate-500">No video URL</p>
                  )}
                </div>

                <div>
                  <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <FaImage /> Activation photos ({photos.length})
                  </h3>
                  {photos.length === 0 ? (
                    <p className="text-sm text-slate-500">No photos</p>
                  ) : (
                    <>
                      <div className="mb-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
                        <button
                          type="button"
                          className="block w-full"
                          onClick={() => setLightboxUrl(photos[activePhotoIndex])}
                        >
                          <img
                            src={photos[activePhotoIndex]}
                            alt={`Activation ${activePhotoIndex + 1}`}
                            className="mx-auto max-h-[320px] w-full object-contain"
                          />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {photos.map((url, i) => (
                          <button
                            key={url + i}
                            type="button"
                            onClick={() => setActivePhotoIndex(i)}
                            className={`h-16 w-16 overflow-hidden rounded-lg border-2 ${
                              i === activePhotoIndex ? 'border-slate-900' : 'border-transparent'
                            }`}
                          >
                            <img src={url} alt="" className="h-full w-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <div className="grid gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-600 sm:grid-cols-2">
                  <div>
                    <span className="font-semibold text-slate-500">Submitted</span>
                    <p>{formatDate(selected.submittedAt)}</p>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-500">Reviewed</span>
                    <p>{formatDate(selected.reviewedAt)}</p>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-500">Reviewed by</span>
                    <p>{selected.reviewedBy || '—'}</p>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-500">Credit</span>
                    <p>{selected.creditPercentage ?? 40}%</p>
                  </div>
                  {selected.couponCode && (
                    <div className="sm:col-span-2">
                      <span className="font-semibold text-slate-500">Coupon</span>
                      <p className="flex items-center gap-2 font-mono text-sm font-semibold text-emerald-800">
                        <FaTag /> {selected.couponCode}
                        {selected.creditApplied ? ' (redeemed)' : ' (issued)'}
                      </p>
                    </div>
                  )}
                  {selected.reviewNotes && (
                    <div className="sm:col-span-2">
                      <span className="font-semibold text-slate-500">Review notes</span>
                      <p>{selected.reviewNotes}</p>
                    </div>
                  )}
                </div>

                {(selected.status === 'submitted' || selected.status === 'underReview') && (
                  <div className="space-y-3 border-t border-slate-100 pt-4">
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        className="f-button inline-flex items-center gap-2 !bg-emerald-600 hover:!bg-emerald-700"
                        disabled={actionLoading}
                        onClick={() => handleApprove(selected)}
                      >
                        {actionLoading ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                        Approve + issue 40% coupon
                      </button>
                      <button
                        type="button"
                        className="f-button f-button-secondary inline-flex items-center gap-2 !text-rose-700"
                        disabled={actionLoading}
                        onClick={() => setShowRejectForm((v) => !v)}
                      >
                        <FaTimes /> Reject
                      </button>
                    </div>

                    {showRejectForm && (
                      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-rose-800">
                          Reason (optional)
                        </label>
                        <textarea
                          className="f-input mb-3 w-full"
                          rows={3}
                          value={rejectNotes}
                          onChange={(e) => setRejectNotes(e.target.value)}
                          placeholder="Short reason for support…"
                        />
                        <button
                          type="button"
                          className="f-button !bg-rose-600 hover:!bg-rose-700"
                          disabled={actionLoading}
                          onClick={() => handleReject(selected)}
                        >
                          Confirm reject
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {selected.status === 'approved' && !selected.couponCode && (
                  <button
                    type="button"
                    className="f-button inline-flex items-center gap-2"
                    disabled={actionLoading}
                    onClick={() => handleApprove(selected)}
                  >
                    <FaTag /> Issue missing coupon
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightboxUrl(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute right-4 top-4 rounded-lg bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/20"
            onClick={() => setLightboxUrl(null)}
          >
            Close
          </button>
          <img
            src={lightboxUrl}
            alt="Full size"
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
